import React from 'react';
import clsx from 'clsx';
import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip';

import { color } from '@strava/ui-tokens/js';

import { Button } from '../Button/Button';
import { Typography } from '../Typography/Typography';

import styles from './Tooltip.module.scss';

// Anatomy
//
// <Tooltip>
//   <Tooltip.Trigger />
//   <Tooltip.Popup />
// </Tooltip>

/**
 * =====================================
 * Tooltip (Root)
 *
 * Groups all parts of the tooltip.
 * Doesn’t render its own HTML element.
 * =====================================
 * */
export const Tooltip = ({ children, ...props }: Tooltip.Props) => {
  return (
    <BaseTooltip.Root delay={50} {...props}>
      {children}
    </BaseTooltip.Root>
  );
};
Tooltip.displayName = 'Tooltip';

export namespace Tooltip {
  export type Props = BaseTooltip.Root.Props;

  // sub-component props
  export type TriggerProps = TooltipTrigger.Props;
  export type ContentProps = TooltipContent.Props;
}

/**
 * =====================================
 * Tooltip Provider
 *
 * Provides a shared delay for multiple tooltips.
 * The grouping logic ensures that once a tooltip
 * becomes visible, the adjacent tooltips will be
 * shown instantly.
 * =====================================
 * */
export const TooltipProvider = ({ children, ...props }: Tooltip.Props) => {
  return <BaseTooltip.Provider {...props}>{children}</BaseTooltip.Provider>;
};
Tooltip.displayName = 'TooltipProvider';

export namespace TooltipProvider {
  export type Props = BaseTooltip.Provider.Props;
}

/**
 * =====================================
 * Tooltip Trigger
 *
 * An element to attach the tooltip to.
 * Renders a <button> element.
 * =====================================
 * */
export const TooltipTrigger = (props: TooltipTrigger.Props) => (
  <BaseTooltip.Trigger
    render={(renderProps) => {
      return (
        <Button type="button" {...renderProps}>
          {renderProps.children}
        </Button>
      );
    }}
    {...props}
  />
);
TooltipTrigger.displayName = 'TooltipTrigger';

export namespace TooltipTrigger {
  export type Props = BaseTooltip.Trigger.Props;
}

/**
 * =====================================
 * Tooltip Content
 *
 * Composes Base UI's Tooltip.Portal,
 * Tooltip.Positioner, Tooltip.Popup and
 * Tooltip.Arrow into a single component.
 * =====================================
 * */
export const TooltipContent = ({
  children,
  portalProps,
  positionerProps,
  popupProps,
  ...props
}: TooltipContent.Props) => {
  return (
    <BaseTooltip.Portal {...portalProps}>
      <BaseTooltip.Positioner
        sideOffset={10}
        arrowPadding={0}
        {...positionerProps}
      >
        <BaseTooltip.Popup
          className={clsx(styles.popup, popupProps?.className)}
          {...popupProps}
        >
          <BaseTooltip.Arrow className={clsx(styles.arrow)}>
            <svg
              width="20"
              height="10"
              viewBox="0 0 20 10"
              fill={color.colorExtendedNeutralN1}
            >
              <path d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z" />
            </svg>
          </BaseTooltip.Arrow>
          <Typography fontSize="2xs" color="coreWhite" {...props}>
            {children}
          </Typography>
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
};
TooltipContent.displayName = 'TooltipContent';

export namespace TooltipContent {
  export type Props = Typography.Props & {
    /** Base UI's <Tooltip.Portal /> props. See: [Portal Arg Table](https://base-ui.com/react/components/tooltip#portal).  */
    portalProps?: BaseTooltip.Portal.Props;
    /** Base UI's <Tooltip.Positioner /> props. See: [Positioner Arg Table](https://base-ui.com/react/components/tooltip#positioner).  */
    positionerProps?: BaseTooltip.Positioner.Props;
    /** Base UI's <Tooltip.Popup /> props. See: [Popup Arg Table](https://base-ui.com/react/components/tooltip#popup).  */
    popupProps?: BaseTooltip.Popup.Props;
  };
}

/**
 * =====================================
 * Compound Components
 * =====================================
 * */

Tooltip.Provider = TooltipProvider;
Tooltip.Trigger = TooltipTrigger;
Tooltip.Content = TooltipContent;
